import React, { useContext, useEffect, useState } from 'react';
import { computeCrustArea, computeCrustVolume, computePerimeter, computeSplitPercentages } from '../helpers/math';
import { getStoredValue, storeValue } from '../helpers/storage';
import Info from './Info';
import PropTypes from 'prop-types';
import { SettingsContext } from '../providers/SettingsProvider';
import { getMoldTypeAsString } from '../helpers/consts-and-types';
import styles from './Crust.module.scss';
import useDebouncedEffect from '../hooks/use-debounced-effect';
import useLocalize from '../hooks/use-localize';
import useMeasureChangeHandler from '../hooks/use-measure-change-handler';

// Default width for crust is 2 mm
const DEFAULT_CRUST_WIDTH = 2;

const Crust = ({ height, length, moldType, radius, setArea, setCrustUrl, setVolume, side, width }) => {
  const { localize } = useLocalize();
  const { isCrustEnabled } = useContext(SettingsContext);
  const [crustWidth, setCrustWidth] = useState(() => getStoredValue(side, getMoldTypeAsString(moldType), 'crust', 0));
  const [perimeter, setPerimeter] = useState(0);
  const [bottomVolume, setBottomVolume] = useState(0);
  const [stripVolume, setStripVolume] = useState(0);

  const handleOnChange = useMeasureChangeHandler(setCrustWidth);

  useEffect(() => {
    if (isCrustEnabled && crustWidth === 0) {
      const newCrustWidth = getStoredValue(side, getMoldTypeAsString(moldType), 'crust', DEFAULT_CRUST_WIDTH);
      setCrustWidth(newCrustWidth > 0 ? newCrustWidth : DEFAULT_CRUST_WIDTH);
    }

    storeValue(side, getMoldTypeAsString(moldType), [['crust', isCrustEnabled ? crustWidth : 0]]);
  }, [crustWidth, isCrustEnabled]);

  useDebouncedEffect(() => setCrustUrl(isCrustEnabled ? crustWidth.toString() : '0'), [crustWidth, isCrustEnabled]);

  useEffect(() => {
    if (crustWidth > 0) {
      setPerimeter(computePerimeter(moldType, { length, radius, width }));
      const { bottom, strip } = computeCrustArea(moldType, { height, length, radius, width });
      setArea(bottom + strip);
    } else {
      setPerimeter(0);
      setArea(0);
    }
  }, [crustWidth, height, length, moldType, radius, setArea, width]);

  // crustWidth is in mm but everything else is in cm
  useEffect(() => {
    if (crustWidth > 0) {
      const { bottom, strip } = computeCrustVolume(moldType, {
        crustWidth: crustWidth / 10,
        height,
        length,
        radius,
        width,
      });
      setBottomVolume(bottom);
      setStripVolume(strip);
      setVolume(bottom + strip);
    } else {
      setVolume(0);
    }
  }, [crustWidth, height, length, moldType, radius, setVolume, width]);

  const { percentage1: stripPct, percentage2: bottomPct } = React.useMemo(() => computeSplitPercentages(stripVolume, bottomVolume), [bottomVolume, stripVolume]);

  if (!isCrustEnabled) {
    return null;
  }

  return (
    <>
      <div>{localize('crust.label')}</div>
      <div className={styles.measure}>{crustWidth}&nbsp;mm</div>
      <input max='6' min='1' onChange={handleOnChange} step='.5' type='range' value={crustWidth} />
      <div className={`${styles.detailLabel} ${styles.smallTopMargin}`}>{localize('crust.strip.size')}</div>
      <div className={`${styles.detailValue} ${styles.smallTopMargin}`}>
        {Math.ceil(perimeter) + 1} x {height}&nbsp;cm
        <Info>{localize('crust.strip.hint')}</Info>
      </div>
      <div className={styles.detailLabel}>{localize('crust.strip_bottom.breakdown')}</div>
      <div className={styles.detailValue}>
        {stripPct}%&nbsp;/&nbsp;{bottomPct}%
        <Info>
          <div>{localize('crust.strip_bottom.hint')}</div>
          <div className={styles.illustration}>
            <div className={styles.doughWrapper}>
              <div className={`${styles.doughSheet} ${styles.strips}`}>
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
              <div className={styles.title}>{localize('crust.strip_bottom.strips', { stripPct })}</div>
            </div>
            <div className={styles.doughWrapper}>
              <div className={`${styles.doughSheet} ${styles.discs}`}>
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
              <div className={styles.title}>{localize('crust.strip_bottom.bottoms', { bottomPct })}</div>
            </div>
          </div>
        </Info>
      </div>
    </>
  );
};

Crust.propTypes = {
  height: PropTypes.number.isRequired,
  length: PropTypes.number,
  moldType: PropTypes.number.isRequired,
  radius: PropTypes.number,
  setArea: PropTypes.func.isRequired,
  setCrustUrl: PropTypes.func.isRequired,
  setVolume: PropTypes.func.isRequired,
  side: PropTypes.string.isRequired,
  width: PropTypes.number,
};

export default Crust;
